/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { useOnMount, useResizeObserver, useUniqueId } from "abreact/utils/AbHooks";
import { Assert } from "abweb/utils/Assert";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import * as React from "react";
import ReactDOM from "react-dom";
const LOG = LogUtils.getLogger(__filename);
const CSS_CONTAINER = "ab-inline-panel-layout";
const CSS_EXPANDED = "ab-inline-panel-layout-expanded";
const CSS_ITEM = "ab-inline-panel-layout-item";
const CSS_ITEM_CONTENT = "ab-inline-panel-layout-item-content";
const CSS_PANEL = "ab-inline-panel-layout-panel";
const CSS_PANEL_CONTAINER = "ab-inline-panel-layout-panel-container";
const CSS_PRIMARY = "ab-inline-panel-layout-primary";

// Private context to share state.
const LayoutContext = /*#__PURE__*/React.createContext(undefined);
LayoutContext.displayName = "LayoutContext (private)";
const PanelContext = /*#__PURE__*/React.createContext(null);
PanelContext.displayName = "PanelContext (private)";
function PanelInternal(props) {
  const expansionContext = React.useContext(InlinePanelLayout.ExpansionContext);
  const panelElement = React.useContext(PanelContext);

  // Delay first render of contents until the panel is expanded the first time.
  const [expandedPanelElement, setExpandedPanelElement] = React.useState(null);
  React.useEffect(() => {
    if (panelElement == null) {
      setExpandedPanelElement(null);
    } else if (expansionContext.expanded) {
      setExpandedPanelElement(panelElement);
    }
  }, [expansionContext.expanded, panelElement]);
  React.useEffect(() => {
    if (expansionContext.expanded && expandedPanelElement != null) {
      FocusUtils.setFocus(PopupUtils.findFirstTabbableElement(expandedPanelElement, ["input", "textarea"]));
    }
  }, [expansionContext.expanded, expandedPanelElement]);
  if (expandedPanelElement == null) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return /*#__PURE__*/ReactDOM.createPortal(props.children, expandedPanelElement);
}
function ItemInternal(props) {
  const id = useUniqueId();
  const [offsetLeft, setOffsetLeft] = React.useState(0);
  const layoutContext = React.useContext(LayoutContext);
  Assert.assert(layoutContext != null, "InlinePanelLayout.Item should be inside a InlinePanelLayout.Container.");
  const {
    width,
    expand,
    collapse,
    expandedId
  } = layoutContext;
  const [primary, setPrimary] = React.useState(false);
  const expanded = expandedId === id;

  // Reference to the component's root element.
  const itemElementRef = React.useRef(null);
  // Reference (and state) to the component's inline panel element.
  const panelElementRef = useResizeObserver(() => setOffsetLeft(itemElementRef.current?.offsetLeft ?? 0));
  const [panelElement, setPanelElement] = React.useState(null);

  // Warn of wrong usage in development mode.
  useOnMount(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (itemElementRef.current == null) return;
    const parentIsLayout = itemElementRef.current.parentElement?.classList.contains(CSS_CONTAINER) ?? false;
    if (!parentIsLayout) {
      LOG.error("InlinePanelLayout.Item must be a direct DOM child of InlinePanelLayout.Container.");
    }
  });

  // Use intersection observer to determine when we are out of the layout's bounds,
  // to recompute the offset left so we can adjust properly.
  useOnMount(() => {
    setOffsetLeft(itemElementRef.current?.offsetLeft ?? 0);
    setPanelElement(panelElementRef.current);
    // Find out when the panel moves with relation to its parent layout so we can
    // make sure it remains inside the parent layout.
    if (panelElementRef.current == null) {
      return;
    }
    const layout = panelElementRef.current.closest(`.${CSS_CONTAINER}`);
    if (layout == null) {
      return;
    }
    const observer = new IntersectionObserver(() => {
      setOffsetLeft(itemElementRef.current?.offsetLeft ?? 0);
    }, {
      root: layout,
      threshold: 1.0
    });
    observer.observe(panelElementRef.current);
    return () => observer.disconnect();
  });
  const expansion = React.useMemo(() => {
    return {
      expand: () => expand(id),
      collapse,
      expanded,
      primary
    };
  }, [id, expand, collapse, expanded, primary]);

  // The 1 at the end accounts for rounding errors.
  const leftAdjustment = 0 - offsetLeft + 1;
  const onFocus = React.useCallback(event => {
    const panel = event.target?.closest(`.${CSS_PANEL}`);
    setPrimary(panel === panelElementRef.current);
  }, []);
  const onBlur = React.useCallback(() => setPrimary(false), []);
  return /*#__PURE__*/React.createElement("div", {
    id: props.id,
    ref: itemElementRef,
    "data-layout-id": id,
    "data-test-id": props.dataTestId,
    className: CssUtils.composeClassNames(CSS_ITEM, props.className, expanded && CSS_EXPANDED, primary && CSS_PRIMARY),
    style: props.style
  }, /*#__PURE__*/React.createElement(PanelContext.Provider, {
    value: panelElement
  }, /*#__PURE__*/React.createElement(InlinePanelLayout.ExpansionContext.Provider, {
    value: expansion
  }, /*#__PURE__*/React.createElement("div", {
    className: CSS_ITEM_CONTENT,
    onFocus: onFocus,
    onBlur: onBlur
  }, props.children))), /*#__PURE__*/React.createElement("div", {
    className: CSS_PANEL_CONTAINER
  }, /*#__PURE__*/React.createElement("div", {
    className: CSS_PANEL,
    ref: panelElementRef,
    style: {
      width: `${width}px`,
      left: `${leftAdjustment}px`
    }
  })));
}
function ContainerInternal(props) {
  const [expandedId, setExpandedId] = React.useState("");
  const [width, setWidth] = React.useState(0);
  const ref = useResizeObserver((_dimensions, element) => {
    const style = getComputedStyle(element);
    // The 2 at the end handles rounding errors.
    setWidth(element.offsetWidth - parseFloat(style.borderLeftWidth) - parseFloat(style.borderRightWidth) - 2);
  });
  const expand = React.useCallback(id => {
    setExpandedId(id);
  }, []);
  const collapse = React.useCallback(() => {
    setExpandedId("");
  }, []);
  const layoutContext = React.useMemo(() => {
    return {
      width,
      expand,
      collapse,
      expandedId
    };
  }, [width, expand, collapse, expandedId]);
  return /*#__PURE__*/React.createElement(LayoutContext.Provider, {
    value: layoutContext
  }, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    id: props.id,
    className: StringUtils.cssClassStr(CSS_CONTAINER, props.className),
    "data-test-id": props.dataTestId,
    style: props.style
  }, props.children));
}

/**
 * Provides React components to render a flex layout where items can have expandable inline panels.
 */
export let InlinePanelLayout;
(function (_InlinePanelLayout) {
  /**
   * Functions used to expand / collapse the inline panels and expansion state.
   */

  /**
   * Context containing utilities and state to handle inline panel expansion.
   */
  const ExpansionContext = _InlinePanelLayout.ExpansionContext = /*#__PURE__*/React.createContext({
    expand: () => {},
    collapse: () => {},
    expanded: false,
    primary: false
  });
  ExpansionContext.displayName = "InlinePanelLayout.ExpansionContext";
  /**
   * The container for the layout.
   * You can freely set its CSS styling, except for align-items, display and position.
   * You can include any number of components as children, which will be aligned in a flex container.
   * If you want an item to have an expandable panel, you should include InlinePanelLayout.Element as a direct
   * child of the container.
   */
  const Container = _InlinePanelLayout.Container = ContainerInternal;
  const Item = _InlinePanelLayout.Item = ItemInternal;
  const Panel = _InlinePanelLayout.Panel = PanelInternal;
})(InlinePanelLayout || (InlinePanelLayout = {}));
//# sourceMappingURL=InlinePanelLayout.js.map
